// 연습 일지 (내 일정 > 연습 일지) — 연속 연습일·월 달력·곡별 연습 체크·연습 시간·메모
// 색은 앱 공용 테마(pianoTheme)를 그대로 따른다.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, ButtonBase, CircularProgress, IconButton, Menu, MenuItem, TextField, Typography, useTheme,
} from '@mui/material';
import {
  CheckRounded, AddRounded, EditOutlined, AccessTimeRounded, EmojiEventsOutlined,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import api from '../api/axios';
import { useDialog } from '../contexts/DialogProvider';
import MonthCalendarNav from '../components/MonthCalendarNav';
import { useMetronome, formatDuration } from '../metronome/MetronomeProvider';
import MetronomeIcon from '../metronome/MetronomeIcon';
import Stepper from '../metronome/Stepper';
import PieceDialog from '../metronome/PieceDialog';
import PracticeChecks from './PracticeChecks';
import TimeDialog from './TimeDialog';
import { piano } from '../theme/pianoTheme';

const MAX_TARGET = 30;
const SAVE_DELAY = 400;
const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
const RADIUS = '12px';
// 달력 칸 진하기 0~3단계 (건반 흑백 단계)
const HEAT = {
  light: ['#EFEDE6', '#D9D6CC', '#8A877E', piano.ink],
  dark: ['#262624', '#4A4945', '#8A877E', '#F2F0EA'],
};

// 달력 칸 진하기: 연습 시간(분) + 완료 횟수×3을 합친 점수
function heatLevel(entries) {
  if (!entries?.length) return 0;
  const score = entries.reduce((s, e) => s + e.seconds / 60 + e.done_count * 3, 0);
  if (score <= 0) return 0;
  if (score < 20) return 1;
  if (score < 45) return 2;
  return 3;
}

function StatCard({ label, value }) {
  return (
    <Box sx={{ flex: 1, minWidth: 0, textAlign: 'center', py: 1.25, borderRadius: RADIUS, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography noWrap sx={{ fontWeight: 800, fontSize: '1.25rem', lineHeight: 1.3 }}>{value}</Typography>
    </Box>
  );
}

function PieceRow({ piece, entry, editable, onUpdate, onEditTime, onEditPiece, onMetronome }) {
  const isFree = !piece || piece.id === 0;
  const target = entry ? entry.target_count : (piece?.default_target_count ?? 5);
  const done = entry?.done_count ?? 0;
  const title = isFree ? '곡 지정 없음' : piece.title;
  const reached = target > 0 && done >= target;
  const tempo = entry?.bpm
    ? (entry.bpm_start && entry.bpm_start !== entry.bpm ? `♩ ${entry.bpm_start} → ${entry.bpm}` : `♩ ${entry.bpm}`)
    : null;

  return (
    <Box sx={{ py: 1.5, '& + &': { borderTop: '1px dashed', borderColor: 'divider' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
            {title}
            {piece?.is_archived && <Typography component="span" variant="caption" color="text.secondary"> · 보관됨</Typography>}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', minHeight: 20 }}>
            {tempo && <Typography variant="caption" color="text.secondary">{tempo}{piece?.target_bpm ? ` · 목표 ${piece.target_bpm}` : ''}</Typography>}
            {!tempo && piece?.target_bpm && <Typography variant="caption" color="text.secondary">목표 ♩ {piece.target_bpm}</Typography>}
            {entry?.seconds > 0 && (
              <ButtonBase
                disabled={!editable}
                onClick={() => onEditTime(entry, title)}
                sx={{ gap: 0.25, color: 'text.secondary', fontSize: '0.75rem', borderRadius: '6px' }}
              >
                <AccessTimeRounded sx={{ fontSize: 14 }} />{formatDuration(entry.seconds)}
              </ButtonBase>
            )}
          </Box>
        </Box>
        {!isFree && editable && !piece.is_archived && (
          <>
            <IconButton size="small" aria-label={`${title} 메트로놈으로 연습`} onClick={() => onMetronome(piece)}>
              <MetronomeIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <IconButton size="small" aria-label={`${title} 수정`} onClick={() => onEditPiece(piece)}>
              <EditOutlined sx={{ fontSize: 19 }} />
            </IconButton>
          </>
        )}
      </Box>

      {!isFree && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1, mb: 1.25 }}>
            <Typography variant="body2" color="text.secondary">
              목표 횟수
            </Typography>
            {editable ? (
              <Stepper
                label="목표 횟수"
                value={Math.max(1, target)}
                min={1}
                max={MAX_TARGET}
                format={(v) => `${v}회`}
                onChange={(v) => onUpdate({ target_count: v, done_count: Math.min(done, v) })}
              />
            ) : (
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{target}회</Typography>
            )}
          </Box>
          {target > 0 && (
            <PracticeChecks
              target={target}
              done={done}
              title={title}
              onChange={(v) => editable && onUpdate({ target_count: target, done_count: v })}
            />
          )}
          {editable && (
            <Button
              fullWidth
              variant={reached ? 'outlined' : 'contained'}
              disabled={reached}
              startIcon={reached ? <EmojiEventsOutlined /> : <CheckRounded />}
              onClick={() => onUpdate({ target_count: target, done_count: Math.min(target, done + 1) })}
              sx={{ mt: 1.5, height: 44, borderRadius: RADIUS, fontWeight: 700 }}
            >
              {reached ? `목표 달성 ${done}/${target}` : `한 번 완료 ${done}/${target}`}
            </Button>
          )}
        </>
      )}
    </Box>
  );
}

export default function PracticeJournal({ year, month, setYearMonth }) {
  const m = useMetronome();
  const { toast } = useDialog();
  const { palette } = useTheme();
  const heat = HEAT[palette.mode];

  const [data, setData] = useState(null);
  const [showLoader, setShowLoader] = useState(false);
  const [selected, setSelected] = useState(null);
  const [addedRows, setAddedRows] = useState({}); // 지난 날짜에 기록하려고 추가한 곡 { date: [pieceId] }
  const [memo, setMemo] = useState('');
  const [memoSaved, setMemoSaved] = useState(true);
  const [addAnchor, setAddAnchor] = useState(null);
  const [pieceDialog, setPieceDialog] = useState(null); // { piece } | {}
  const [timeDialog, setTimeDialog] = useState(null); // { entry, title } | {}
  const saveTimers = useRef({});
  const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;

  const load = useCallback(async () => {
    const loaderTimer = setTimeout(() => setShowLoader(true), 250);
    try {
      const res = await api.get('/practice-journal/month', { params: { month: monthKey } });
      setData(res.data);
    } catch (e) {
      toast('연습 일지를 불러오지 못했어요.', 'error');
    } finally {
      clearTimeout(loaderTimer);
      setShowLoader(false);
    }
  }, [monthKey, toast]);

  useEffect(() => { load(); }, [load, m.journalRevision]);

  // 달이 바뀌면: 이번 달이면 오늘, 아니면 마지막 기록일(없으면 1일)을 고른다
  useEffect(() => {
    if (!data) return;
    setSelected((cur) => {
      if (cur && cur.startsWith(monthKey)) return cur;
      if (data.today.startsWith(monthKey)) return data.today;
      const last = [...data.entries].reverse().find((e) => e.seconds > 0 || e.done_count > 0);
      return last ? last.date : `${monthKey}-01`;
    });
  }, [data, monthKey]);

  const note = data?.notes.find((n) => n.date === selected);
  useEffect(() => {
    setMemo(note?.memo || '');
    setMemoSaved(true);
  }, [selected, note?.memo]);

  const byDate = useMemo(() => {
    const map = {};
    (data?.entries || []).forEach((e) => { (map[e.date] = map[e.date] || []).push(e); });
    return map;
  }, [data]);

  const pieceById = useMemo(() => {
    const map = {};
    (data?.pieces || []).forEach((p) => { map[p.id] = p; });
    m.pieces.forEach((p) => { map[p.id] = p; });
    return map;
  }, [data, m.pieces]);

  // 기록 저장: 화면은 즉시 바꾸고, 같은 곡의 연속 탭은 모아서 마지막 값만 보낸다
  const updateEntry = (date, pieceId, patch) => {
    setData((d) => {
      const exists = d.entries.some((e) => e.date === date && e.piece_id === pieceId);
      const entries = exists
        ? d.entries.map((e) => (e.date === date && e.piece_id === pieceId ? { ...e, ...patch } : e))
        : [...d.entries, { id: `tmp-${date}-${pieceId}`, date, piece_id: pieceId, seconds: 0, done_count: 0, target_count: 0, ...patch }];
      return { ...d, entries };
    });
    const key = `${date}:${pieceId}`;
    const pending = { ...(saveTimers.current[key]?.patch || {}), ...patch };
    clearTimeout(saveTimers.current[key]?.timer);
    saveTimers.current[key] = {
      patch: pending,
      timer: setTimeout(async () => {
        delete saveTimers.current[key];
        try {
          const res = await api.put('/practice-journal/entries', { date, piece_id: pieceId, ...pending });
          setData((d) => ({
            ...d,
            entries: d.entries.map((e) => (e.date === date && e.piece_id === pieceId ? { ...res.data, ...pickLocal(e, saveTimers.current[key]) } : e)),
          }));
        } catch (e) {
          toast(e.response?.data?.message || '기록을 저장하지 못했어요.', 'error');
          load();
        }
      }, SAVE_DELAY),
    };
  };

  // 저장 응답이 오는 사이에 또 탭했다면 화면의 최신 값을 유지
  function pickLocal(entry, pendingSave) {
    if (!pendingSave) return {};
    const out = {};
    Object.keys(pendingSave.patch).forEach((k) => { out[k] = entry[k]; });
    return out;
  }

  // 화면을 떠날 때 보내지 못한 저장을 바로 보낸다
  useEffect(() => () => {
    Object.entries(saveTimers.current).forEach(([key, { patch, timer }]) => {
      clearTimeout(timer);
      const [date, pieceId] = key.split(':');
      api.put('/practice-journal/entries', { date, piece_id: Number(pieceId), ...patch }).catch(() => {});
    });
  }, []);

  const saveMemo = async () => {
    if (memoSaved || !selected) return;
    try {
      const res = await api.put(`/practice-journal/notes/${selected}`, { memo });
      setData((d) => ({
        ...d,
        notes: res.data ? [...d.notes.filter((n) => n.date !== selected), res.data] : d.notes.filter((n) => n.date !== selected),
      }));
      setMemoSaved(true);
    } catch (e) {
      toast(e.response?.data?.message || '메모를 저장하지 못했어요.', 'error');
    }
  };

  const submitTime = async ({ pieceId, minutes }) => {
    const target = timeDialog;
    setTimeDialog(null);
    try {
      if (target.entry) {
        updateEntry(selected, target.entry.piece_id, { seconds: minutes * 60 });
      } else {
        await api.post('/practice-journal/entries/time', { date: selected, piece_id: pieceId, seconds: minutes * 60 });
        load();
      }
    } catch (e) {
      toast(e.response?.data?.message || '연습 시간을 저장하지 못했어요.', 'error');
    }
  };

  const openMetronome = (piece) => {
    m.applyPiece(piece);
    m.openSheet();
  };

  if (!data) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  const today = data.today;
  const isToday = selected === today;
  const editable = !!selected && selected <= today && selected >= dayjs(today).subtract(365, 'day').format('YYYY-MM-DD');
  const dayEntries = byDate[selected] || [];

  // 오늘은 내 곡 전체를 보여주고(아직 안 한 곡도 바로 체크), 지난 날은 기록이 있는 곡 + 추가한 곡만
  const rowIds = [];
  const pushId = (id) => { if (!rowIds.includes(id)) rowIds.push(id); };
  if (isToday) m.pieces.forEach((p) => pushId(p.id));
  dayEntries.forEach((e) => pushId(e.piece_id));
  (addedRows[selected] || []).forEach(pushId);
  const rows = rowIds
    .map((id) => ({ piece: id === 0 ? null : pieceById[id], entry: dayEntries.find((e) => e.piece_id === id), id }))
    .filter((r) => r.id === 0 || r.piece)
    // 곡 지정 없음 행은 시간 기록이 있을 때만
    .filter((r) => r.id !== 0 || r.entry?.seconds > 0);
  const addable = m.pieces.filter((p) => !rowIds.includes(p.id));
  const daySeconds = dayEntries.reduce((s, e) => s + e.seconds, 0);

  // 달력
  const first = dayjs(`${monthKey}-01`);
  const cells = [];
  for (let i = 0; i < first.day(); i++) cells.push(null);
  for (let d = 1; d <= first.daysInMonth(); d++) cells.push(first.date(d).format('YYYY-MM-DD'));

  return (
    <>
      <Box sx={{ position: 'relative', pb: 2 }}>
        {showLoader && (
          <Box sx={{ position: 'absolute', inset: 0, zIndex: 5, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(0,0,0,0.08)' }}>
            <CircularProgress />
          </Box>
        )}

        {/* 요약 */}
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <StatCard label="연속 연습" value={`${data.stats.streak}일`} />
          <StatCard label="이번 달" value={`${data.stats.month_days}일`} />
          <StatCard label="이번 달 시간" value={data.stats.month_seconds ? formatDuration(data.stats.month_seconds) : '0분'} />
        </Box>

        {/* 월 달력 (진할수록 많이 연습) */}
        <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: RADIUS, px: 1, py: 1.5, mb: 1.5 }}>
          <MonthCalendarNav year={year} month={month} onChange={setYearMonth} allowYearView>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', mb: 0.5 }}>
              {WEEKDAYS.map((d) => (
                <Typography key={d} variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>{d}</Typography>
              ))}
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
              {cells.map((date, i) => {
                if (!date) return <Box key={`e${i}`} />;
                const level = heatLevel(byDate[date]);
                const future = date > today;
                const isSel = date === selected;
                return (
                  <ButtonBase
                    key={date}
                    disabled={future}
                    onClick={() => setSelected(date)}
                    aria-label={`${dayjs(date).format('M월 D일')} 연습 기록`}
                    aria-pressed={isSel}
                    sx={{
                      height: 38,
                      borderRadius: '8px',
                      bgcolor: heat[level],
                      color: level >= 2 ? (palette.mode === 'dark' ? 'background.default' : 'background.paper') : 'text.primary',
                      opacity: future ? 0.35 : 1,
                      border: '2px solid',
                      borderColor: isSel ? 'secondary.main' : 'transparent',
                      fontSize: '0.8125rem',
                      fontWeight: date === today ? 800 : 500,
                      textDecoration: date === today ? 'underline' : 'none',
                      textUnderlineOffset: '3px',
                    }}
                  >
                    {Number(date.slice(8))}
                  </ButtonBase>
                );
              })}
            </Box>
          </MonthCalendarNav>
        </Box>

        {/* 선택한 날 */}
        {selected && (
          <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: RADIUS, px: 2, py: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '1rem' }}>
                {dayjs(selected).format('M월 D일')} ({WEEKDAYS[dayjs(selected).day()]}){isToday ? ' 오늘' : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AccessTimeRounded sx={{ fontSize: 16 }} />{daySeconds ? formatDuration(daySeconds) : '0분'}
              </Typography>
            </Box>

            {rows.length === 0 && (
              <Box sx={{ py: 3, textAlign: 'center' }}>
                <Typography sx={{ fontWeight: 700 }}>
                  {m.pieces.length === 0 ? '연습할 곡을 추가해 보세요' : '이 날은 기록이 없어요'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {m.pieces.length === 0
                    ? '곡마다 목표 횟수를 정하고 한 번 칠 때마다 체크해요.'
                    : '아래에서 곡을 골라 지난 연습을 기록할 수 있어요.'}
                </Typography>
              </Box>
            )}

            {rows.map(({ id, piece, entry }) => (
              <PieceRow
                key={id}
                piece={piece}
                entry={entry}
                editable={editable}
                onUpdate={(patch) => updateEntry(selected, id, patch)}
                onEditTime={(e, title) => setTimeDialog({ entry: e, title })}
                onEditPiece={(p) => setPieceDialog({ piece: p })}
                onMetronome={openMetronome}
              />
            ))}

            {editable && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                {isToday || m.pieces.length === 0 ? (
                  <Button size="small" startIcon={<AddRounded />} onClick={() => setPieceDialog({})}>내 곡 추가</Button>
                ) : (
                  addable.length > 0 && (
                    <Button size="small" startIcon={<AddRounded />} onClick={(e) => setAddAnchor(e.currentTarget)}>곡 골라 기록</Button>
                  )
                )}
                <Button size="small" startIcon={<AccessTimeRounded />} onClick={() => setTimeDialog({})}>연습 시간 추가</Button>
              </Box>
            )}
            <Menu anchorEl={addAnchor} open={!!addAnchor} onClose={() => setAddAnchor(null)}>
              {addable.map((p) => (
                <MenuItem
                  key={p.id}
                  onClick={() => {
                    setAddedRows((r) => ({ ...r, [selected]: [...(r[selected] || []), p.id] }));
                    setAddAnchor(null);
                  }}
                >
                  {p.title}
                </MenuItem>
              ))}
            </Menu>

            {/* 메모 */}
            <TextField
              fullWidth
              multiline
              minRows={2}
              disabled={!editable}
              label="메모"
              placeholder="왼손 아르페지오가 아직 고르지 않음. 다음 레슨 때 페달 질문하기"
              value={memo}
              onChange={(e) => { setMemo(e.target.value); setMemoSaved(false); }}
              onBlur={saveMemo}
              inputProps={{ maxLength: 2000 }}
              helperText={memoSaved ? ' ' : '입력을 마치면 저장돼요'}
              sx={{ mt: 2 }}
            />
          </Box>
        )}
      </Box>

      <PieceDialog
        open={!!pieceDialog}
        piece={pieceDialog?.piece}
        onClose={() => setPieceDialog(null)}
      />
      <TimeDialog
        open={!!timeDialog}
        entry={timeDialog?.entry}
        pieceTitle={timeDialog?.title}
        pieces={m.pieces}
        onClose={() => setTimeDialog(null)}
        onSubmit={submitTime}
      />
    </>
  );
}
